import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";
import BannedService from "../../setting/banned_words/BannedService";

const DictionaryApiService = {
    /**
     * GET /home/dic/{language}/{word}/play — returns decrypted body { url } (MP3) when available.
     */
    playWordAudio: async (word, language = 'english') => {
        try {
            const w = String(word || '').trim();
            if (!w) {
                throw new Error('Word is required');
            }
            const lang = String(language || 'english').trim() || 'english';
            const result = await ApiService.getData(`/home/dic/${lang}/${encodeURIComponent(w)}/play`);
            if (result && typeof result === 'object' && typeof result.url === 'string' && result.url.trim()) {
                return result.url.trim();
            }
            if (result && typeof result === 'object') {
                const errMsg = result.message || result.error || result.msg;
                if (typeof errMsg === 'string' && errMsg.trim()) {
                    throw new Error(errMsg.trim());
                }
            }
            return null;
        } catch (error) {
            Logger.error(`Error fetching word audio for "${word}":`, error);
            throw error;
        }
    },

    getWordDefinition: async (word, language = 'english') => {
        try {
            // Use local API endpoint (ApiService.getData handles decryption automatically)
            const result = await ApiService.getData(`/home/dic/${language}/${encodeURIComponent(word)}/single`);
            
            // Local API returns single object, convert to external API format for consistency
            const normalizedResult = {
                word: result.word,
                meanings: []
            };
            
            // Convert meaning object to meanings array format
            if (result.meaning) {
                Object.entries(result.meaning).forEach(([partOfSpeech, definition]) => {
                    // Skip 'title' key as it's not a part of speech
                    if (partOfSpeech !== 'title') {
                        normalizedResult.meanings.push({
                            partOfSpeech: partOfSpeech,
                            definitions: [{
                                definition: definition,
                                example: null,
                                synonyms: [],
                                antonyms: []
                            }]
                        });
                    }
                });
            }
            
            return [normalizedResult];
        } catch (error) {
            Logger.error(`Error fetching word definition for "${word}":`, error);
            throw error;
        }
    },

    getMultipleWordsInfo: async (words, language = 'english', validOnly = false) => {
        try {
            const wordsParams = words.map(word => `words=${encodeURIComponent(word)}`).join('&');
            const url = `/home/dic/${language}/multiple?${wordsParams}&validOnly=${validOnly}`;
            const result = await ApiService.getData(url);
            return result;
        } catch (error) {
            Logger.error(`Error fetching multiple words info:`, error);
            throw error;
        }
    },

    getMultipleWordsInfoFromCache: async (words, language = 'english', validOnly = false) => {
        try {
            const wordsParams = words.map(word => `words=${encodeURIComponent(word)}`).join('&');
            const url = `/home/dic-cache/${language}/multiple?${wordsParams}&validOnly=${validOnly}`;
            const result = await ApiService.getData(url);
            return result;
        } catch (error) {
            Logger.error(`Error fetching multiple words info from cache:`, error);
            throw error;
        }
    },

    getWordsByPattern: async (pattern, language = 'english') => {
        try {
            // Use ApiService.getData to handle decryption automatically
            const result = await ApiService.getData(`/home/dic/${language}/${encodeURIComponent(pattern)}/pattern`);
            
            // Handle NO_DATA or error responses
            if (result && typeof result === 'object' && result.code === 'NO_DATA') {
                return [];
            }
            // Ensure we always return an array
            if (Array.isArray(result)) {
                return result;
            } else if (result && typeof result === 'object') {
                // If it's a single object, wrap it in an array
                return [result];
            } else {
                // If it's not an object or array, return empty array
                Logger.warn(`Unexpected response format for pattern "${pattern}":`, result);
                return [];
            }
        } catch (error) {
            Logger.error(`Error fetching words by pattern "${pattern}":`, error);
            // Return empty array instead of throwing to prevent breaking the calling code
            return [];
        }
    },

    // Wrapper function to normalize DictionaryApiService.getWordsByPattern response to match DatamuseService.wildcardSearch format
    wildcardSearch: async (pattern, language = 'english') => {
        try {
            const results = await DictionaryApiService.getWordsByPattern(pattern, language);
            
            // Check if results is an array, if not return empty array
            if (!Array.isArray(results)) {
                return [];
            }
            
            // Transform the response to match DatamuseService.wildcardSearch format
            // Filter out items without a word (e.g., from NO_DATA or malformed responses)
            return results
                .filter(item => item && item.word)
                .map(item => ({
                    word: item.word,
                    score: item.info?.score || 0,
                    tags: item.info?.frequency ? [`f:${item.info.frequency}`] : []
                }));
        } catch (error) {
            Logger.error(`Error in wildcardSearch wrapper for pattern "${pattern}":`, error);
            // Return empty array instead of throwing to prevent breaking the calling code
            return [];
        }
    },

    getDifficultyLevel: async (words, language = 'english') => {
        try {
            if (!words || words.length === 0) {
                return { difficultyLevel: 'EASY', wordsCount: 0, language };
            }

            const requestBody = {
                words: words,
                language: language
            };

            const result = await ApiService.postData("/v1/dic/difficulty-level", requestBody);
            return result;
        } catch (error) {
            Logger.error(`Error fetching difficulty level:`, error);
            // Return default on error
            return { difficultyLevel: 'EASY', wordsCount: words ? words.length : 0, language };
        }
    },

    getDifficultyLevelPolicy: async () => {
        try {
            const result = await ApiService.getData("/v1/dic/difficulty-level/policy");
            return result;
        } catch (error) {
            Logger.error(`Error fetching difficulty level policy:`, error);
            throw error;
        }
    },

    // Get word meaning for editing (returns raw API response)
    getWord: async (word, language = 'english') => {
        try {
            const result = await ApiService.getData(`/v1/dic/${language}/${encodeURIComponent(word.toLowerCase())}`);
            return result;
        } catch (error) {
            Logger.error(`Error fetching word for editing "${word}":`, error);
            throw error;
        }
    },

    // Translate word to a specific language
    translateWord: async (word, sourceLanguage, targetLanguage) => {
        try {
            const result = await ApiService.postData(
                `/home/dic/${sourceLanguage}/${encodeURIComponent(word)}/translate/${targetLanguage}`,
                {}
            );
            
            // Check if the response indicates failure
            if (result && result.code === "FAILURE") {
                throw new Error(result.message || `Failed to translate word "${word}" from ${sourceLanguage} to ${targetLanguage}`);
            }
            
            return result;
        } catch (error) {
            Logger.error(`Error translating word "${word}" from ${sourceLanguage} to ${targetLanguage}:`, error);
            throw error;
        }
    },

    // Update word meaning
    // The backend API accepts only meaning as JsonNode, but the backend code should:
    // 1. Fetch the existing Dictionary entity by word and language
    // 2. Update only the meaning field
    // 3. Preserve wordNorm and other fields when saving
    // If the backend is throwing "wordNorm is null" error, the backend code needs to be fixed
    // to properly fetch the existing entity before updating
    updateWordMeaning: async (word, meaning, language = 'english') => {
        try {
            // Send only the meaning as the API signature specifies
            // The backend should handle fetching the existing entity and preserving wordNorm
            const result = await ApiService.putData(
                `/v1/dic/${language}/${encodeURIComponent(word)}`,
                meaning
            );
            return result;
        } catch (error) {
            Logger.error(`Error updating word meaning for "${word}":`, error);
            throw error;
        }
    },

    // Helper function to convert strength to numeric score
    _strengthToScore: (strength) => {
        if (strength === 'strong') return 100;
        if (strength === 'medium') return 50;
        return 25; // default for weak or unknown
    },

    // Helper function to recursively extract all words from nested relations structure
    _extractWordsFromRelations: (item, wordsMap = new Map(), visited = new Set()) => {
        if (!item || !item.word) return wordsMap;
        
        const wordKey = item.word.toLowerCase();
        // Avoid infinite loops by tracking visited words
        if (visited.has(wordKey)) return wordsMap;
        visited.add(wordKey);
        
        // Add the word itself (use Map to avoid duplicates by word key)
        const score = DictionaryApiService._strengthToScore(item.strength || 'medium');
        // Keep the highest score if word appears multiple times
        if (!wordsMap.has(wordKey) || wordsMap.get(wordKey).score < score) {
            wordsMap.set(wordKey, {
                word: item.word,
                score: score
            });
        }
        
        // Recursively process nested relations
        if (item.relations) {
            DictionaryApiService._extractWordsFromRelations(item.relations, wordsMap, visited);
        }
        
        return wordsMap;
    },

    // Get word associations (synonyms, antonyms, and related words) from the API
    // Returns format: { synonyms: [{ word, score }], antonyms: [{ word, score }], relatedWords: [{ word, score }] }
    getWordAssociations: async (word, language = 'english') => {
        try {
            const result = await ApiService.getData(`/v1/dic/${language}/${encodeURIComponent(word.toLowerCase())}/assoc`);
            
            const synonyms = [];
            const antonyms = [];
            const relatedWords = [];
            
            // Process synonyms array (can have nested relations)
            if (result.synonyms && Array.isArray(result.synonyms)) {
                const synonymsMap = new Map();
                result.synonyms.forEach(item => {
                    // Extract all words from nested structure
                    const extracted = DictionaryApiService._extractWordsFromRelations(item, new Map(), new Set());
                    // Merge into main synonyms map
                    extracted.forEach((wordObj, key) => {
                        // Keep the highest score if word appears multiple times
                        if (!synonymsMap.has(key) || synonymsMap.get(key).score < wordObj.score) {
                            synonymsMap.set(key, wordObj);
                        }
                    });
                });
                // Convert map values to array
                synonyms.push(...Array.from(synonymsMap.values()));
            }
            
            // Process antonyms array (if available)
            if (result.antonyms && Array.isArray(result.antonyms)) {
                const antonymsMap = new Map();
                result.antonyms.forEach(item => {
                    // Extract all words from nested structure
                    const extracted = DictionaryApiService._extractWordsFromRelations(item, new Map(), new Set());
                    // Merge into main antonyms map
                    extracted.forEach((wordObj, key) => {
                        // Keep the highest score if word appears multiple times
                        if (!antonymsMap.has(key) || antonymsMap.get(key).score < wordObj.score) {
                            antonymsMap.set(key, wordObj);
                        }
                    });
                });
                // Convert map values to array
                antonyms.push(...Array.from(antonymsMap.values()));
            }
            
            // Process related_words array (can have nested relations)
            if (result.related_words && Array.isArray(result.related_words)) {
                const relatedWordsMap = new Map();
                result.related_words.forEach(item => {
                    // Extract all words from nested structure
                    const extracted = DictionaryApiService._extractWordsFromRelations(item, new Map(), new Set());
                    // Merge into main related words map
                    extracted.forEach((wordObj, key) => {
                        // Keep the highest score if word appears multiple times
                        if (!relatedWordsMap.has(key) || relatedWordsMap.get(key).score < wordObj.score) {
                            relatedWordsMap.set(key, wordObj);
                        }
                    });
                });
                // Convert map values to array
                relatedWords.push(...Array.from(relatedWordsMap.values()));
            }
            
            return {
                synonyms: synonyms,
                antonyms: antonyms,
                relatedWords: relatedWords
            };
        } catch (error) {
            Logger.error(`Error fetching word associations for "${word}":`, error);
            // Return empty arrays instead of throwing to prevent breaking the calling code
            return {
                synonyms: [],
                antonyms: [],
                relatedWords: []
            };
        }
    },

    // Check banned words for a list of words.
    // When difficultyLevel is EASY, words in high_frequency category are not flagged (common words allowed in easy puzzles).
    checkBannedWords: async (words, language = 'english', isKids = false, difficultyLevel = null) => {
        try {
            if (!words || words.length === 0 || !language) {
                return {
                    bannedWords: [],
                    totalChecked: 0,
                    bannedCount: 0,
                    validCount: 0
                };
            }

            const wordsToCheck = words.slice(0, 100);
            const wordsParams = wordsToCheck.map(word => `words=${encodeURIComponent(word)}`).join('&');
            const isKidsParam = isKids ? `&isKids=true` : '';
            const url = `/home/dic/${language}/check-banned?${wordsParams}${isKidsParam}`;
            
            const result = await ApiService.getData(url);
            
            if (result && result.bannedWords && Array.isArray(result.bannedWords)) {
                let bannedWords = result.bannedWords;
                // When puzzle is EASY, exclude high_frequency banned words
                if (difficultyLevel === 'EASY' && bannedWords.length > 0) {
                    try {
                        const bannedList = await BannedService.requestList(language);
                        const highFrequencyWords = (bannedList || [])
                            .filter(b => b && b.category === 'high_frequency')
                            .map(b => (b.word || '').toLowerCase())
                            .filter(Boolean);
                        if (highFrequencyWords.length > 0) {
                            bannedWords = bannedWords.filter(w => {
                                const wordStr = (typeof w === 'string' ? w : (w.word || w)).toLowerCase();
                                return !highFrequencyWords.includes(wordStr);
                            });
                        }
                    } catch (err) {
                        Logger.warn('Could not filter high_frequency for EASY puzzle:', err);
                    }
                }
                return {
                    bannedWords,
                    totalChecked: result.totalChecked || wordsToCheck.length,
                    bannedCount: bannedWords.length,
                    validCount: result.validCount ?? (wordsToCheck.length - bannedWords.length)
                };
            }
            return {
                bannedWords: [],
                totalChecked: wordsToCheck.length,
                bannedCount: 0,
                validCount: wordsToCheck.length
            };
        } catch (error) {
            Logger.error(`Error checking banned words:`, error);
            return {
                bannedWords: [],
                totalChecked: words ? words.length : 0,
                bannedCount: 0,
                validCount: words ? words.length : 0
            };
        }
    },

    getTrends: async (words, language = 'english') => {
        try {
            if (!words || words.length === 0) {
                throw new Error("Words array cannot be empty");
            }

            const requestBody = {
                words: words,
                language: language
            };

            const result = await ApiService.postData("/v1/dic/trends", requestBody);
            return result;
        } catch (error) {
            Logger.error(`Error fetching trends:`, error);
            throw error;
        }
    },

    getNgram: async (words, language = 'english') => {
        try {
            if (!words || words.length === 0) {
                throw new Error("Words array cannot be empty");
            }

            const requestBody = {
                words: words,
                language: language
            };

            const result = await ApiService.postData("/v1/dic/ngram", requestBody);
            return result;
        } catch (error) {
            Logger.error(`Error fetching ngram:`, error);
            throw error;
        }
    },

};

export default DictionaryApiService;

